import { extractEndpointIdFromTab } from '../../../utils/extractEndpointIdFromTab';
import type {
  MainContentTab,
  MainContentTabData,
  TabNavigationState,
} from '../../layoutStore';
import { migrateSavedPinnedTabsKeys } from './layout.saved-pinned-tabs.migration';

export const LAYOUT_PERSIST_VERSION = 12;

const EPHEMERAL_TAB_TYPES: MainContentTab[] = [
  'hitl-edit',
  'http-viewer',
  'node-args-viewer',
  'browser-stream',
  'skill-editor',
];

type SplitterPanelConfig = {
  id: string | number;
  size: number;
  minSize?: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isSplitterPanelArray(value: unknown): value is SplitterPanelConfig[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        isRecord(item) &&
        (typeof item.id === 'string' || typeof item.id === 'number') &&
        typeof item.size === 'number',
    )
  );
}

function hasSplitterPanelIds({
  sizes,
  expectedIds,
}: {
  sizes: SplitterPanelConfig[];
  expectedIds: string[];
}): boolean {
  const ids = sizes.map((panel) => String(panel.id));

  return (
    ids.length === expectedIds.length &&
    expectedIds.every((expectedId) => ids.includes(expectedId))
  );
}

function reconcileSplitterPanelSizes({
  current,
  defaults,
}: {
  current: SplitterPanelConfig[];
  defaults: SplitterPanelConfig[];
}): SplitterPanelConfig[] {
  const defaultById = new Map(
    defaults.map((panel) => [String(panel.id), panel]),
  );

  const reconciled = current.map((panel) => {
    const fallback = defaultById.get(String(panel.id));

    return {
      id: panel.id,
      size: panel.size > 0 ? panel.size : (fallback?.size ?? 50),
      minSize: fallback?.minSize ?? panel.minSize,
    };
  });

  if (reconciled.some((panel) => panel.size < 12)) {
    return defaults;
  }

  const totalSize = reconciled.reduce((sum, panel) => sum + panel.size, 0);

  if (totalSize < 95 || totalSize > 105) {
    return defaults;
  }

  return reconciled;
}

const DEFAULT_MATCH_REPLACE_RULES_PREVIEW: SplitterPanelConfig[] = [
  { id: 'rules-table', size: 32, minSize: 20 },
  { id: 'preview', size: 68, minSize: 38 },
];

const DEFAULT_MATCH_REPLACE_RULES_EDITOR: SplitterPanelConfig[] = [
  { id: 'main', size: 72, minSize: 48 },
  { id: 'rule-editor', size: 28, minSize: 22 },
];

const DEFAULT_MATCH_REPLACE_REQUESTS_PREVIEW: SplitterPanelConfig[] = [
  { id: 'requests', size: 22, minSize: 14 },
  { id: 'http-preview', size: 78, minSize: 42 },
];

const DEFAULT_MATCH_REPLACE_ORIGINAL_TRANSFORMED: SplitterPanelConfig[] = [
  { id: 'original', size: 50, minSize: 28 },
  { id: 'transformed', size: 50, minSize: 28 },
];

function migrateRulesPreviewSplitter(value: unknown): SplitterPanelConfig[] {
  if (!isSplitterPanelArray(value)) {
    return DEFAULT_MATCH_REPLACE_RULES_PREVIEW;
  }

  const renamed = value.map((panel) =>
    String(panel.id) === 'rules' ? { ...panel, id: 'rules-table' } : panel,
  );

  if (
    !hasSplitterPanelIds({
      sizes: renamed,
      expectedIds: ['rules-table', 'preview'],
    })
  ) {
    return DEFAULT_MATCH_REPLACE_RULES_PREVIEW;
  }

  return reconcileSplitterPanelSizes({
    current: renamed,
    defaults: DEFAULT_MATCH_REPLACE_RULES_PREVIEW,
  });
}

function migrateRulesEditorSplitter(value: unknown): SplitterPanelConfig[] {
  if (!isSplitterPanelArray(value)) {
    return DEFAULT_MATCH_REPLACE_RULES_EDITOR;
  }

  if (value.length === 3) {
    return DEFAULT_MATCH_REPLACE_RULES_EDITOR;
  }

  if (
    hasSplitterPanelIds({
      sizes: value,
      expectedIds: ['rules-table', 'rule-editor'],
    })
  ) {
    return reconcileSplitterPanelSizes({
      current: value.map((panel) =>
        String(panel.id) === 'rules-table' ? { ...panel, id: 'main' } : panel,
      ),
      defaults: DEFAULT_MATCH_REPLACE_RULES_EDITOR,
    });
  }

  if (
    !hasSplitterPanelIds({ sizes: value, expectedIds: ['main', 'rule-editor'] })
  ) {
    return DEFAULT_MATCH_REPLACE_RULES_EDITOR;
  }

  return reconcileSplitterPanelSizes({
    current: value,
    defaults: DEFAULT_MATCH_REPLACE_RULES_EDITOR,
  });
}

function migrateRequestsPreviewSplitter(value: unknown): SplitterPanelConfig[] {
  if (!isSplitterPanelArray(value)) {
    return DEFAULT_MATCH_REPLACE_REQUESTS_PREVIEW;
  }

  const renamed = value.map((panel) =>
    String(panel.id) === 'preview' ? { ...panel, id: 'http-preview' } : panel,
  );

  if (
    !hasSplitterPanelIds({
      sizes: renamed,
      expectedIds: ['requests', 'http-preview'],
    })
  ) {
    return DEFAULT_MATCH_REPLACE_REQUESTS_PREVIEW;
  }

  return reconcileSplitterPanelSizes({
    current: renamed,
    defaults: DEFAULT_MATCH_REPLACE_REQUESTS_PREVIEW,
  });
}

function migrateOriginalTransformedSplitter(
  value: unknown,
): SplitterPanelConfig[] {
  if (!isSplitterPanelArray(value)) {
    return DEFAULT_MATCH_REPLACE_ORIGINAL_TRANSFORMED;
  }

  if (
    !hasSplitterPanelIds({
      sizes: value,
      expectedIds: ['original', 'transformed'],
    })
  ) {
    return DEFAULT_MATCH_REPLACE_ORIGINAL_TRANSFORMED;
  }

  return reconcileSplitterPanelSizes({
    current: value,
    defaults: DEFAULT_MATCH_REPLACE_ORIGINAL_TRANSFORMED,
  });
}

function extractIdFromTabPrefix({
  tab,
  prefix,
}: {
  tab: Record<string, unknown>;
  prefix: string;
}): string | undefined {
  const tabData = isRecord(tab.data) ? tab.data : undefined;

  if (tabData && typeof tabData.id === 'string') {
    return tabData.id;
  }

  if (tabData && typeof tabData.entityId === 'string') {
    return tabData.entityId;
  }

  if (typeof tab.id === 'string' && tab.id.startsWith(`${prefix}-`)) {
    return tab.id.slice(prefix.length + 1);
  }

  return undefined;
}

function migratePersistedMainContentTabs(tabs: unknown): unknown {
  if (!Array.isArray(tabs)) {
    return tabs;
  }

  return tabs.map((tab) => {
    if (!isRecord(tab) || typeof tab.type !== 'string') {
      return tab;
    }

    const tabData = isRecord(tab.data) ? tab.data : {};

    switch (tab.type) {
      case 'endpoint': {
        const endpointId = extractEndpointIdFromTab({ tab });

        if (!endpointId) return tab;

        return { ...tab, data: { ...tabData, endpointId } };
      }

      case 'webpage': {
        if (tabData.id && typeof tabData.id === 'string') return tab;
        const id = extractIdFromTabPrefix({ tab, prefix: 'webpage' });

        if (!id) return tab;

        return { ...tab, data: { ...tabData, id } };
      }

      case 'signal': {
        if (tabData.signalId && typeof tabData.signalId === 'string')
          return tab;
        const signalId = extractIdFromTabPrefix({ tab, prefix: 'signal' });

        if (!signalId) return tab;

        return { ...tab, data: { ...tabData, signalId } };
      }

      case 'finding': {
        if (tabData.findingId && typeof tabData.findingId === 'string')
          return tab;
        let findingId: string | undefined;

        if (typeof tab.id === 'string' && tab.id.startsWith('finding-edit-')) {
          findingId = tab.id.slice('finding-edit-'.length);
        } else {
          findingId = extractIdFromTabPrefix({ tab, prefix: 'finding' });
        }

        if (!findingId) return tab;

        return { ...tab, data: { ...tabData, findingId } };
      }

      case 'traces':
      case 'executions': {
        const existingThreadId =
          tabData.threadId && typeof tabData.threadId === 'string'
            ? tabData.threadId
            : undefined;
        let threadId = existingThreadId;

        if (!threadId) {
          for (const prefix of ['executions', 'traces', 'trace']) {
            threadId = extractIdFromTabPrefix({ tab, prefix });
            if (threadId) break;
          }
        }

        const migratedTab: Record<string, unknown> = {
          ...tab,
          type: 'executions',
          data: { ...tabData, threadId: threadId ?? tabData.threadId },
        };

        if (threadId) {
          migratedTab.id = `executions-${threadId}`;
        }

        return migratedTab;
      }

      default:
        return tab;
    }
  });
}

function normalizeExecutionsTabId(id: string): string {
  if (id.startsWith('trace-')) return `executions-${id.slice(6)}`;
  if (id.startsWith('traces-')) return `executions-${id.slice(7)}`;

  return id;
}

export function migrateLayoutPersistedState(
  persistedState: unknown,
  _version: number,
): unknown {
  if (!isRecord(persistedState)) {
    return persistedState;
  }

  const migratedTabs = migratePersistedMainContentTabs(
    persistedState.mainContentTabs,
  );

  const activeMainContentTabId =
    typeof persistedState.activeMainContentTabId === 'string'
      ? normalizeExecutionsTabId(persistedState.activeMainContentTabId)
      : persistedState.activeMainContentTabId;

  const tabHistory = Array.isArray(persistedState.tabHistory)
    ? persistedState.tabHistory.map((id: unknown) =>
        typeof id === 'string' ? normalizeExecutionsTabId(id) : id,
      )
    : persistedState.tabHistory;

  const tabNavigationByTabId = isRecord(persistedState.tabNavigationByTabId)
    ? Object.fromEntries(
        Object.entries(persistedState.tabNavigationByTabId).map(
          ([tabId, state]) => [normalizeExecutionsTabId(tabId), state],
        ),
      )
    : {};

  const migratedPersistableTabs = Array.isArray(migratedTabs)
    ? migratedTabs.filter(
        (tab) =>
          !(
            isRecord(tab) &&
            EPHEMERAL_TAB_TYPES.includes(tab.type as MainContentTab)
          ),
      )
    : [];

  const seenPersistableTabIds = new Set<string>();
  const persistableTabs = migratedPersistableTabs.filter((tab) => {
    if (!isRecord(tab) || typeof tab.id !== 'string') return true;
    const normalizedTabId = normalizeExecutionsTabId(tab.id);

    if (seenPersistableTabIds.has(normalizedTabId)) return false;
    seenPersistableTabIds.add(normalizedTabId);

    return true;
  });

  const persistableTabIds = new Set(
    persistableTabs
      .filter((tab) => isRecord(tab) && typeof tab.id === 'string')
      .map((tab) => (tab as { id: string }).id),
  );

  const reconciledActiveTabId =
    typeof activeMainContentTabId === 'string' &&
    persistableTabIds.has(activeMainContentTabId)
      ? activeMainContentTabId
      : undefined;

  const reconciledTabHistory = Array.isArray(tabHistory)
    ? tabHistory.filter(
        (id: unknown) => typeof id === 'string' && persistableTabIds.has(id),
      )
    : [];

  const hydratedTabNavigationByTabId = persistableTabs.reduce(
    (accumulator, tab) => {
      if (!isRecord(tab) || typeof tab.id !== 'string') return accumulator;
      const normalizedTabId = normalizeExecutionsTabId(tab.id);
      const existingState = isRecord(tabNavigationByTabId[normalizedTabId])
        ? tabNavigationByTabId[normalizedTabId]
        : undefined;

      if (existingState) {
        accumulator[normalizedTabId] = existingState as TabNavigationState;

        return accumulator;
      }

      if (typeof tab.title !== 'string' || typeof tab.type !== 'string') {
        return accumulator;
      }

      accumulator[normalizedTabId] = {
        entries: [
          {
            pageId: normalizedTabId,
            title: tab.title,
            type: tab.type as MainContentTab,
            data: isRecord(tab.data)
              ? (tab.data as MainContentTabData)
              : undefined,
          },
        ],
        currentIndex: 0,
      };

      return accumulator;
    },
    {} as Record<string, TabNavigationState>,
  );

  return {
    ...persistedState,
    mainContentTabs: persistableTabs,
    activeMainContentTabId: reconciledActiveTabId,
    tabHistory: reconciledTabHistory,
    tabNavigationByTabId: hydratedTabNavigationByTabId,
    savedPinnedTabs: migrateSavedPinnedTabsKeys(persistedState.savedPinnedTabs),
    matchReplaceRulesPreviewSplitterSizes: migrateRulesPreviewSplitter(
      persistedState.matchReplaceRulesPreviewSplitterSizes,
    ),
    matchReplaceRulesEditorSplitterSizes: migrateRulesEditorSplitter(
      persistedState.matchReplaceRulesEditorSplitterSizes,
    ),
    matchReplaceRequestsPreviewSplitterSizes: migrateRequestsPreviewSplitter(
      persistedState.matchReplaceRequestsPreviewSplitterSizes,
    ),
    matchReplaceOriginalTransformedSplitterSizes:
      migrateOriginalTransformedSplitter(
        persistedState.matchReplaceOriginalTransformedSplitterSizes,
      ),
  };
}
